import React, { useState, useEffect, useCallback } from 'react';
import { get, post, patch, del } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { useDemandPlanning } from '../hooks/useDemandPlanning';
import DemandPlanningTab from '../components/scheduling/DemandPlanningTab';
import EmployeesTab from '../components/scheduling/EmployeesTab';
import RolesTab from '../components/scheduling/RolesTab';
import ShiftsTab from '../components/scheduling/ShiftsTab';
import ScheduleTab from '../components/scheduling/ScheduleTab';
import RequestsTab from '../components/scheduling/RequestsTab';
import {
  EmployeeModal,
  RoleModal,
  ShiftModal,
  PasswordModal,
  RejectModal,
  AvailabilityModal
} from '../components/scheduling/SchedulingModals';
import './Scheduling.css';

const Scheduling = () => {
  const { getAuthToken } = useAuth();
  const [activeTab, setActiveTab] = useState('demand');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [weekSchedules, setWeekSchedules] = useState({});
  const [availabilityRequests, setAvailabilityRequests] = useState([]);
  const [lastOptimizationResult, setLastOptimizationResult] = useState(null);

  // Week navigation - get Monday of current week
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  const [weekStartDate, setWeekStartDate] = useState(() => {
    const monday = getMonday(new Date());
    return monday.toISOString().split('T')[0];
  });

  // Modal states
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  // Form states
  const [employeeForm, setEmployeeForm] = useState({ name: '', email: '', phone: '', role_ids: [], hourly_rate: '' });
  const [roleForm, setRoleForm] = useState({ name: '', tph: '', description: '', color: '#4CAF50' });
  const [shiftForm, setShiftForm] = useState({
    name: '',
    start_time: '09:00',
    end_time: '17:00',
    days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    color: '#4CAF50'
  });

  // Request approval modal states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Employee availability modal states
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedEmployeeAvailability, setSelectedEmployeeAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  // Employee filter states
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeRoleFilter, setEmployeeRoleFilter] = useState('');
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState('');
  const [employeeSortBy, setEmployeeSortBy] = useState('name');
  const [employeeSortOrder, setEmployeeSortOrder] = useState('asc');

  // Use the demand planning hook
  const demandPlanning = useDemandPlanning(getAuthToken, setLoading, setError, setSuccess);

  const tabs = [
    { id: 'demand', label: 'Demand Planning', icon: '📈' },
    { id: 'schedule', label: 'Schedule', icon: '📅' },
    { id: 'employees', label: 'Employees', icon: '👥' },
    { id: 'roles', label: 'Roles', icon: '🎭' },
    { id: 'shifts', label: 'Shifts', icon: '⏰' },
    { id: 'requests', label: 'Requests', icon: '📋' },
  ];

  // Fetch functions
  const fetchEmployees = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/employees',
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setEmployees(data.employees || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, [getAuthToken]);

  const fetchRoles = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/roles',
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setRoles(data.roles || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }, [getAuthToken]);

  const fetchShifts = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/shifts',
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setShifts(data.shifts || []);
    } catch (err) {
      console.error('Error fetching shifts:', err);
    }
  }, [getAuthToken]);

  const fetchWeekSchedules = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/schedules/week?start_date=${weekStartDate}`,
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setWeekSchedules(data.week_schedules || {});
    } catch (err) {
      console.error('Error fetching week schedules:', err);
    }
  }, [getAuthToken, weekStartDate]);

  const fetchAvailabilityRequests = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/availability-requests?status=pending',
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setAvailabilityRequests(data.requests || []);
    } catch (err) {
      console.error('Error fetching availability requests:', err);
    }
  }, [getAuthToken]);

  const viewEmployeeAvailability = async (employee) => {
    setAvailabilityLoading(true);
    setSelectedEmployeeAvailability({ employee, availability: null });
    setShowAvailabilityModal(true);

    try {
      const token = await getAuthToken();
      const response = await get({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/employees/${employee.employee_id}/availability`,
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setSelectedEmployeeAvailability({
        employee,
        availability: data.weekly || data.availability || null
      });
    } catch (err) {
      console.error('Error fetching employee availability:', err);
      setSelectedEmployeeAvailability({
        employee,
        availability: null,
        error: 'Failed to load availability'
      });
    }
    setAvailabilityLoading(false);
  };

  // Fetch availability requests on initial load for badge count
  useEffect(() => {
    fetchAvailabilityRequests();
  }, [fetchAvailabilityRequests]);

  useEffect(() => {
    fetchRoles();
    fetchShifts();
    if (activeTab === 'employees') {
      fetchEmployees();
    } else if (activeTab === 'schedule') {
      fetchWeekSchedules();
    } else if (activeTab === 'requests') {
      fetchAvailabilityRequests();
    } else if (activeTab === 'demand') {
      demandPlanning.fetchDemandData();
    }
  }, [activeTab, fetchEmployees, fetchRoles, fetchShifts, fetchWeekSchedules, fetchAvailabilityRequests, demandPlanning.fetchDemandData]);

  // Week navigation helpers
  const getWeekDates = () => {
    const dates = [];
    const start = new Date(weekStartDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  };

  const formatWeekLabel = () => {
    const start = new Date(weekStartDate + 'T12:00:00');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `Week of ${months[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()}`;
  };

  const goToPrevWeek = () => {
    const current = new Date(weekStartDate);
    current.setDate(current.getDate() - 7);
    setWeekStartDate(current.toISOString().split('T')[0]);
  };

  const goToNextWeek = () => {
    const current = new Date(weekStartDate);
    current.setDate(current.getDate() + 7);
    setWeekStartDate(current.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    const monday = getMonday(new Date());
    setWeekStartDate(monday.toISOString().split('T')[0]);
  };

  // Employee handlers
  const handleCreateEmployee = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const response = await post({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/employees',
        options: {
          headers: { Authorization: token },
          body: employeeForm
        }
      }).response;
      const data = await response.body.json();
      setTempPassword(data.temporary_password);
      setShowEmployeeModal(false);
      setShowPasswordModal(true);
      fetchEmployees();
      setSuccess('Employee created successfully');
    } catch (err) {
      setError(err.message || 'Failed to create employee');
    }
    setLoading(false);
  };

  const handleUpdateEmployee = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await patch({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/employees/${selectedItem.employee_id}`,
        options: {
          headers: { Authorization: token },
          body: employeeForm
        }
      }).response;
      setShowEmployeeModal(false);
      fetchEmployees();
      setSuccess('Employee updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update employee');
    }
    setLoading(false);
  };

  const handleDeleteEmployee = async (employee) => {
    if (!window.confirm(`Are you sure you want to deactivate ${employee.name}?`)) return;
    try {
      const token = await getAuthToken();
      await del({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/employees/${employee.employee_id}`,
        options: { headers: { Authorization: token } }
      }).response;
      fetchEmployees();
      setSuccess('Employee deactivated');
    } catch (err) {
      setError(err.message || 'Failed to deactivate employee');
    }
  };

  const handleResetPassword = async (employee) => {
    try {
      const token = await getAuthToken();
      const response = await post({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/employees/${employee.employee_id}/reset-password`,
        options: { headers: { Authorization: token } }
      }).response;
      const data = await response.body.json();
      setSelectedItem(employee);
      setTempPassword(data.temporary_password);
      setShowPasswordModal(true);
    } catch (err) {
      setError(err.message || 'Failed to reset password');
    }
  };

  // Role handlers
  const handleCreateRole = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await post({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/roles',
        options: {
          headers: { Authorization: token },
          body: { ...roleForm, tph: parseFloat(roleForm.tph) }
        }
      }).response;
      setShowRoleModal(false);
      fetchRoles();
      setSuccess('Role created successfully');
    } catch (err) {
      setError(err.message || 'Failed to create role');
    }
    setLoading(false);
  };

  const handleUpdateRole = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await patch({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/roles/${selectedItem.role_id}`,
        options: {
          headers: { Authorization: token },
          body: { ...roleForm, tph: parseFloat(roleForm.tph) }
        }
      }).response;
      setShowRoleModal(false);
      fetchRoles();
      setSuccess('Role updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update role');
    }
    setLoading(false);
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Are you sure you want to deactivate ${role.name}?`)) return;
    try {
      const token = await getAuthToken();
      await del({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/roles/${role.role_id}`,
        options: { headers: { Authorization: token } }
      }).response;
      fetchRoles();
      setSuccess('Role deactivated');
    } catch (err) {
      setError(err.message || 'Failed to deactivate role');
    }
  };

  // Shift handlers
  const handleCreateShift = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await post({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/shifts',
        options: {
          headers: { Authorization: token },
          body: shiftForm
        }
      }).response;
      setShowShiftModal(false);
      fetchShifts();
      setSuccess('Shift created successfully');
    } catch (err) {
      setError(err.message || 'Failed to create shift');
    }
    setLoading(false);
  };

  const handleUpdateShift = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await patch({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/shifts/${selectedItem.shift_id}`,
        options: {
          headers: { Authorization: token },
          body: shiftForm
        }
      }).response;
      setShowShiftModal(false);
      fetchShifts();
      setSuccess('Shift updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update shift');
    }
    setLoading(false);
  };

  const handleDeleteShift = async (shift) => {
    if (!window.confirm(`Are you sure you want to deactivate ${shift.name}?`)) return;
    try {
      const token = await getAuthToken();
      await del({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/shifts/${shift.shift_id}`,
        options: { headers: { Authorization: token } }
      }).response;
      fetchShifts();
      setSuccess('Shift deactivated');
    } catch (err) {
      setError(err.message || 'Failed to deactivate shift');
    }
  };

  // Availability request handlers
  const handleApproveRequest = async (request) => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await post({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/availability-requests/${request.request_id}/approve`,
        options: { headers: { Authorization: token } }
      }).response;
      fetchAvailabilityRequests();
      setSuccess(`Approved availability change for ${request.employee_name}`);
    } catch (err) {
      setError(err.message || 'Failed to approve request');
    }
    setLoading(false);
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest) return;
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      await post({
        apiName: 'ChulasEmployeeAPI',
        path: `/v1/admin/scheduling/availability-requests/${selectedRequest.request_id}/reject`,
        options: {
          headers: { Authorization: token },
          body: { reason: rejectReason }
        }
      }).response;
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchAvailabilityRequests();
      setSuccess(`Rejected availability change for ${selectedRequest.employee_name}`);
    } catch (err) {
      setError(err.message || 'Failed to reject request');
    }
    setLoading(false);
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setShowRejectModal(true);
  };

  // Schedule handlers
  const handleGenerateWeek = async (mode = 'ensure_coverage') => {
    setLoading(true);
    setError('');
    try {
      const token = await getAuthToken();
      const response = await post({
        apiName: 'ChulasEmployeeAPI',
        path: '/v1/admin/scheduling/schedules/generate-week',
        options: {
          headers: { Authorization: token },
          body: { start_date: weekStartDate, mode }
        }
      }).response;
      const data = await response.body.json();

      // Store the optimization result for display
      setLastOptimizationResult({
        week_summary: data.week_summary,
        optimization_metrics: data.optimization_metrics,
        employee_hours: data.employee_hours
      });

      fetchWeekSchedules();

      // Build success message with optimization summary
      let message = 'Week optimized successfully';
      if (data.week_summary) {
        const summary = data.week_summary;
        message = `Week optimized: ${summary.total_hours} total hrs`;
        if (summary.overtime_hours > 0) {
          message += ` (${summary.overtime_hours} OT)`;
        }
        if (summary.days_with_gaps > 0) {
          message += ` - ${summary.days_with_gaps} day(s) with coverage gaps`;
        }
      }
      if (data.errors?.length > 0) {
        message = `Generated ${data.dates_generated} schedules (${data.dates_failed} failed)`;
      }
      setSuccess(message);
    } catch (err) {
      setError(err.message || 'Failed to generate week schedules');
    }
    setLoading(false);
  };

  const handlePublishAll = async () => {
    setLoading(true);
    setError('');
    let published = 0;
    let errors = 0;

    try {
      const token = await getAuthToken();
      const dates = getWeekDates();

      for (const dateStr of dates) {
        const daySchedules = weekSchedules[dateStr] || [];
        for (const schedule of daySchedules) {
          if (schedule.status === 'draft') {
            try {
              await post({
                apiName: 'ChulasEmployeeAPI',
                path: `/v1/admin/scheduling/schedules/${schedule.schedule_date}/${schedule.schedule_id}/publish`,
                options: { headers: { Authorization: token } }
              }).response;
              published++;
            } catch (e) {
              errors++;
            }
          }
        }
      }

      fetchWeekSchedules();
      if (errors > 0) {
        setSuccess(`Published ${published} schedules (${errors} failed)`);
      } else if (published > 0) {
        setSuccess(`Published ${published} schedules`);
      } else {
        setSuccess('No draft schedules to publish');
      }
    } catch (err) {
      setError(err.message || 'Failed to publish schedules');
    }
    setLoading(false);
  };

  // Modal openers
  const openCreateEmployeeModal = () => {
    setModalMode('create');
    setSelectedItem(null);
    setEmployeeForm({ name: '', email: '', phone: '', role_ids: [], hourly_rate: '' });
    setShowEmployeeModal(true);
  };

  const openEditEmployeeModal = (employee) => {
    setModalMode('edit');
    setSelectedItem(employee);
    setEmployeeForm({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone || '',
      role_ids: employee.assigned_role_ids || employee.role_ids || [],
      hourly_rate: employee.hourly_rate || ''
    });
    setShowEmployeeModal(true);
  };

  const openCreateRoleModal = () => {
    setModalMode('create');
    setSelectedItem(null);
    setRoleForm({ name: '', tph: '', description: '', color: '#4CAF50' });
    setShowRoleModal(true);
  };

  const openEditRoleModal = (role) => {
    setModalMode('edit');
    setSelectedItem(role);
    setRoleForm({
      name: role.name || '',
      tph: role.tph || '',
      description: role.description || '',
      color: role.color || '#4CAF50'
    });
    setShowRoleModal(true);
  };

  const openCreateShiftModal = () => {
    setModalMode('create');
    setSelectedItem(null);
    setShiftForm({
      name: '',
      start_time: '09:00',
      end_time: '17:00',
      days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
      color: '#4CAF50'
    });
    setShowShiftModal(true);
  };

  const openEditShiftModal = (shift) => {
    setModalMode('edit');
    setSelectedItem(shift);
    setShiftForm({
      name: shift.name || '',
      start_time: shift.start_time || '09:00',
      end_time: shift.end_time || '17:00',
      days_of_week: shift.days_of_week || [],
      color: shift.color || '#4CAF50'
    });
    setShowShiftModal(true);
  };

  const clearEmployeeFilters = () => {
    setEmployeeSearch('');
    setEmployeeRoleFilter('');
    setEmployeeStatusFilter('');
  };

  // Normalize employee data for EmployeesTab (handle both role_ids and assigned_role_ids)
  const normalizedEmployees = employees.map(emp => ({
    ...emp,
    role_ids: emp.assigned_role_ids || emp.role_ids || []
  }));

  // Clear messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className="scheduling-container">
      <div className="page-header">
        <h1>Labor Planning</h1>
        <p className="page-description">Manage employees, roles, shifts, and schedules</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="scheduling-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.id === 'requests' && availabilityRequests.length > 0 && (
              <span className="tab-badge">{availabilityRequests.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="scheduling-content">
        {activeTab === 'demand' && (
          <DemandPlanningTab
            loading={loading}
            demandPlanning={demandPlanning}
          />
        )}

        {activeTab === 'schedule' && (
          <ScheduleTab
            weekSchedules={weekSchedules}
            shifts={shifts}
            loading={loading}
            weekStartDate={weekStartDate}
            goToPrevWeek={goToPrevWeek}
            goToNextWeek={goToNextWeek}
            goToToday={goToToday}
            formatWeekLabel={formatWeekLabel}
            getWeekDates={getWeekDates}
            handleGenerateWeek={handleGenerateWeek}
            handlePublishAll={handlePublishAll}
            lastOptimizationResult={lastOptimizationResult}
          />
        )}

        {activeTab === 'employees' && (
          <EmployeesTab
            employees={normalizedEmployees}
            roles={roles}
            loading={loading}
            employeeSearch={employeeSearch}
            setEmployeeSearch={setEmployeeSearch}
            employeeRoleFilter={employeeRoleFilter}
            setEmployeeRoleFilter={setEmployeeRoleFilter}
            employeeStatusFilter={employeeStatusFilter}
            setEmployeeStatusFilter={setEmployeeStatusFilter}
            employeeSortBy={employeeSortBy}
            setEmployeeSortBy={setEmployeeSortBy}
            employeeSortOrder={employeeSortOrder}
            setEmployeeSortOrder={setEmployeeSortOrder}
            openCreateEmployeeModal={openCreateEmployeeModal}
            openEditEmployeeModal={openEditEmployeeModal}
            handleDeactivateEmployee={handleDeleteEmployee}
            handleResetPassword={handleResetPassword}
            viewEmployeeAvailability={viewEmployeeAvailability}
            clearEmployeeFilters={clearEmployeeFilters}
          />
        )}

        {activeTab === 'roles' && (
          <RolesTab
            roles={roles}
            loading={loading}
            openCreateRoleModal={openCreateRoleModal}
            openEditRoleModal={openEditRoleModal}
            handleDeleteRole={handleDeleteRole}
          />
        )}

        {activeTab === 'shifts' && (
          <ShiftsTab
            shifts={shifts}
            loading={loading}
            openCreateShiftModal={openCreateShiftModal}
            openEditShiftModal={openEditShiftModal}
            handleDeleteShift={handleDeleteShift}
          />
        )}

        {activeTab === 'requests' && (
          <RequestsTab
            availabilityRequests={availabilityRequests}
            loading={loading}
            handleApproveRequest={handleApproveRequest}
            openRejectModal={openRejectModal}
          />
        )}
      </div>

      {/* Modals */}
      <EmployeeModal
        show={showEmployeeModal}
        onClose={() => setShowEmployeeModal(false)}
        mode={modalMode}
        form={employeeForm}
        setForm={setEmployeeForm}
        roles={roles}
        loading={loading}
        onCreate={handleCreateEmployee}
        onUpdate={handleUpdateEmployee}
      />

      <RoleModal
        show={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        mode={modalMode}
        form={roleForm}
        setForm={setRoleForm}
        loading={loading}
        onCreate={handleCreateRole}
        onUpdate={handleUpdateRole}
      />

      <ShiftModal
        show={showShiftModal}
        onClose={() => setShowShiftModal(false)}
        mode={modalMode}
        form={shiftForm}
        setForm={setShiftForm}
        loading={loading}
        onCreate={handleCreateShift}
        onUpdate={handleUpdateShift}
      />

      <PasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        password={tempPassword}
      />

      <RejectModal
        show={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        reason={rejectReason}
        setReason={setRejectReason}
        loading={loading}
        onReject={handleRejectRequest}
      />

      <AvailabilityModal
        show={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        data={selectedEmployeeAvailability}
        loading={availabilityLoading}
      />
    </div>
  );
};

export default Scheduling;
